import React, {useContext, useLayoutEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {TOOLTIP_DELAY} from '../../../../../constants';
import {editSidebarElementTranslationKey, ElementType, elementGroups} from '../../../../../constants/elementGroups';
import {useAddElementAction} from '../../../../../context/AddElementsContext';
import {SlideRefContext} from '../../../../../context/SlideRefContext';
import Button from '../../../../../incase-domain/components/button/Button';
import DropdownMenuCompact, {
    DropdownMenuCompactMenuPosition,
} from '../../../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import {setElementDragPayload} from '../../../../../utils/elementDragDrop';
import TooltipWrapper from '../../../../components/common/TooltipWrapper';

const EXPAND_TRANSITION_MS = 280;
const LAYOUT_SETTLE_MS = 50;
const FLOATING_ELEMENT_SWITCH_TIMEOUT_MS = 280;

function FloatingElementButton({item, onAdd}: {item: ElementType; onAdd: () => void}): JSX.Element {
    const {t} = useTranslation();
    const didDragRef = useRef(false);

    const tooltip = item.shortcut
        ? `${t(editSidebarElementTranslationKey(item.label))} (${item.shortcut.join('+').toUpperCase()})`
        : t(editSidebarElementTranslationKey(item.label));

    return (
        <TooltipWrapper id={`floating-element-${item.label}`} content={tooltip} place="top" delayShow={TOOLTIP_DELAY}>
            <Button
                className="c-floatingElements__button"
                leading={item.icon}
                size="xs"
                variant="ghost"
                color="secondary"
                square
                draggable
                onDragStart={(e) => {
                    didDragRef.current = true;
                    setElementDragPayload(e.dataTransfer, {type: item.type, elementProps: item.elementProps});
                }}
                onDragEnd={() => {
                    requestAnimationFrame(() => {
                        didDragRef.current = false;
                    });
                }}
                onMouseUp={() => {
                    if (didDragRef.current) {
                        didDragRef.current = false;
                        return;
                    }
                    onAdd();
                }}
            />
        </TooltipWrapper>
    );
}

function FloatingElements(): JSX.Element {
    const {t} = useTranslation();
    const addElement = useAddElementAction();
    const {slidesContainerWrapperRef} = useContext(SlideRefContext);
    const [expanded, setExpanded] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const fromSizeRef = useRef<{width: number; height: number} | null>(null);
    const animatingRef = useRef(false);

    // Cap toolbox width to the slide above so items wrap instead of overflowing it.
    useLayoutEffect(() => {
        const slideEl = slidesContainerWrapperRef.current;
        const toolboxEl = rootRef.current;
        if (!slideEl || !toolboxEl) return undefined;

        const syncMaxWidth = (): void => {
            const slideWidth = slideEl.getBoundingClientRect().width;
            if (slideWidth > 0) {
                toolboxEl.style.maxWidth = `${slideWidth}px`;
            }
        };

        syncMaxWidth();
        const resizeObserver = new ResizeObserver(syncMaxWidth);
        resizeObserver.observe(slideEl);
        return () => resizeObserver.disconnect();
    }, [slidesContainerWrapperRef]);

    const handleToggle = (): void => {
        const el = rootRef.current;
        if (!el) {
            setExpanded((prev) => !prev);
            return;
        }

        // Freeze at the current size before content swaps, so we can wait for CSS without a flash.
        const {width, height} = el.getBoundingClientRect();
        fromSizeRef.current = {width, height};
        el.style.transition = 'none';
        el.style.width = `${width}px`;
        el.style.height = `${height}px`;
        el.classList.add('c-floatingElements--animating');
        animatingRef.current = true;
        setExpanded((prev) => !prev);
    };

    useLayoutEffect(() => {
        const el = rootRef.current;
        const from = fromSizeRef.current;
        if (!el || !from || !animatingRef.current) return undefined;

        // Keep clipped at the previous size while layout settles.
        el.style.transition = 'none';
        el.style.width = `${from.width}px`;
        el.style.height = `${from.height}px`;
        el.classList.add('c-floatingElements--animating');

        let cleared = false;
        let fallbackTimer = 0;

        const finish = (): void => {
            if (cleared) return;
            cleared = true;
            el.style.transition = 'none';
            el.style.width = '';
            el.style.height = '';
            el.classList.remove('c-floatingElements--animating');
            el.getBoundingClientRect();
            el.style.transition = '';
            const settled = el.getBoundingClientRect();
            fromSizeRef.current = {width: settled.width, height: settled.height};
            animatingRef.current = false;
        };

        const onTransitionEnd = (event: TransitionEvent): void => {
            if (event.target !== el) return;
            if (event.propertyName !== 'width' && event.propertyName !== 'height') return;
            finish();
        };

        const measureAndAnimate = (): void => {
            if (cleared) return;

            // Measure natural size capped by slide max-width (set via ResizeObserver).
            el.style.width = 'max-content';
            el.style.height = 'auto';
            const to = el.getBoundingClientRect();

            el.style.width = `${from.width}px`;
            el.style.height = `${from.height}px`;
            // Force layout so the transition starts from `from`, not the max-content measure.
            el.getBoundingClientRect();

            if (to.width < 1 || (Math.abs(from.width - to.width) < 1 && Math.abs(from.height - to.height) < 1)) {
                finish();
                return;
            }

            el.style.transition = `width ${EXPAND_TRANSITION_MS}ms ease, height ${EXPAND_TRANSITION_MS}ms ease`;
            el.style.width = `${to.width}px`;
            el.style.height = `${to.height}px`;

            el.addEventListener('transitionend', onTransitionEnd);
            fallbackTimer = window.setTimeout(finish, EXPAND_TRANSITION_MS + 50);
        };

        const settleTimer = window.setTimeout(measureAndAnimate, LAYOUT_SETTLE_MS);

        return () => {
            window.clearTimeout(settleTimer);
            window.clearTimeout(fallbackTimer);
            el.removeEventListener('transitionend', onTransitionEnd);
            el.style.transition = 'none';
            el.style.width = '';
            el.style.height = '';
            el.classList.remove('c-floatingElements--animating');
        };
    }, [expanded]);

    return (
        <div ref={rootRef} className={`c-floatingElements${expanded ? ' c-floatingElements--expanded' : ''}`}>
            <div className="c-floatingElements__body">
                <div className="c-floatingElements__items">
                    {expanded
                        ? elementGroups.map((group, groupIndex) => (
                              <React.Fragment key={group.label}>
                                  {groupIndex > 0 && <div className="c-floatingElements__separator" aria-hidden />}
                                  <div className="c-floatingElements__group">
                                      {group.items.map((item) => (
                                          <FloatingElementButton
                                              key={item.label}
                                              item={item}
                                              onAdd={() => addElement(item.type, item.elementProps)}
                                          />
                                      ))}
                                  </div>
                              </React.Fragment>
                          ))
                        : elementGroups.map((group) => (
                              <DropdownMenuCompact
                                  key={group.label}
                                  items={[
                                      group.items.map((item) => ({
                                          label: t(editSidebarElementTranslationKey(item.label)),
                                          icon: item.icon,
                                          onClick: () => addElement(item.type, item.elementProps),
                                          shortcuts: item.shortcut,
                                          draggable: true,
                                          onDragStart: (e: React.DragEvent<HTMLButtonElement>) => {
                                              setElementDragPayload(e.dataTransfer, {
                                                  type: item.type,
                                                  elementProps: item.elementProps,
                                              });
                                          },
                                      })),
                                  ]}
                                  menuPosition={DropdownMenuCompactMenuPosition.UP}
                                  openOnMouseEnter
                                  openOnMouseEnterDelayMs={FLOATING_ELEMENT_SWITCH_TIMEOUT_MS}
                                  closeOnMouseExit
                                  closeOnMouseExitDelayMs={FLOATING_ELEMENT_SWITCH_TIMEOUT_MS}
                              >
                                  <Button
                                      className="c-floatingElements__button"
                                      leading={group.icon}
                                      size="xs"
                                      variant="ghost"
                                      color="secondary"
                                      square
                                      draggable
                                      onDragStart={(e) => {
                                          const firstItem = group.items[0];
                                          setElementDragPayload(e.dataTransfer, {
                                              type: firstItem.type,
                                              elementProps: firstItem.elementProps,
                                          });
                                      }}
                                  />
                              </DropdownMenuCompact>
                          ))}
                </div>
                <TooltipWrapper
                    id="floating-elements-view-toggle"
                    content={t(expanded ? 'presEditPage.editSidebar.showGroupedElements' : 'presEditPage.editSidebar.showAllElements')}
                    place="top"
                    delayShow={TOOLTIP_DELAY}
                >
                    <Button
                        className="c-floatingElements__button c-floatingElements__toggle"
                        leading={expanded ? 'solar:widget-linear' : 'ph:dots-nine'}
                        size="xs"
                        variant="ghost"
                        color="primary"
                        square
                        onClick={handleToggle}
                    />
                </TooltipWrapper>
            </div>
        </div>
    );
}

export default FloatingElements;
